import * as d3 from "d3";
import ChargeForceGenerator from "./ChargeForceGenerator";
import CollisionForceGenerator from "./CollisionForceGenerator";
import PositioningForceGenerator from "./PositioningForceGenerator";
import ClusterRepulsiveForceGenerator from "./ClusterRepulsiveForceGenerator";
import RadialForceGenerator from "./RadialForceGenerator";
import RadialReactionGenerator from "./RadialReactionGenerator";

export default class ForcesGenerator {
    // Public:
    constructor(cParams) {
        this.saveParams(cParams);
        this.computeNodesSubsets();
        this.setOriginAsGravitationalCenter();
    }

    setGravitationalCenter(center) {
        this._gravitationalCenter = center;
    }

    createChargeForce() {
        let cParams = {
            masterStrength: this._masterParams.chargeStrength,
            slaveStrength: this._slaveParams.chargeStrength,
            distanceMax: this._chargeDistance,
            theta: this._chargeTheta,
        };
        let forceGen = new ChargeForceGenerator(cParams);
        let force = forceGen.compute();
        return force;
    }

    createCollisionForce() {
        let cParams = {
            masterMargin: this._masterParams.collisionMargin,
            slaveMargin: this._slaveParams.collisionMargin,
            masterStrength: this._masterParams.collisionStrength,
            slaveStrength: this._slaveParams.collisionStrength,
            iterations: this._collisionIterations,
        };
        let forceGen = new CollisionForceGenerator(cParams);
        let force = forceGen.compute();
        return force;
    }

    createPositioningForce() {
        let cParams = {
            strength: this._positioningStrength,
            center: this._gravitationalCenter,
        };
        let forceGen = new PositioningForceGenerator(cParams);
        let force = forceGen.compute();
        return force;
    }

    createOrbitalForces() {
        let clusters = this._clusters;
        let namingFunc = id => "orbital" + id;
        let forceCreationFunc = cluster => this.createSingleRadialForce(cluster);
        let forces = this.createArrayForces(clusters, namingFunc, forceCreationFunc);
        return forces;
    }

    createOrbitalReactions() {
        let slaveNodes = this._slaveNodes;
        let forces = [];
        for (let i = 0; i < slaveNodes.length; i++) {
            const node = slaveNodes[i];
            let nodeId = node.number;
            let force = {
                name: "reaction" + nodeId,
                force: this.createSingleRadialReaction(node),
            };
            forces.push(force);
        }
        return forces;
    }

    createSharedNodesOrbitalForces() {
        let sharedNodesClusters = this._sharedNodesClusters;
        let namingFunc = id => "sharedNodesOrbital" + id;
        let forceCreationFunc = cluster => this.createSingleRadialForce(cluster);
        let forces = this.createArrayForces(sharedNodesClusters, namingFunc, forceCreationFunc);
        return forces;
    }

    createClusterRepulsiveForces() {
        let forces = [];
        let clusters = this._clusters;
        for (let i = 0; i < clusters.length; i++) {
            const clusterA = clusters[i];
            for (let j = 0; j < clusters.length; j++) {
                const clusterB = clusters[j];
                const itIsNotItself = i !== j;
                if (itIsNotItself) {
                    const strength = this._clusterRepulsionStrength;
                    let force = {
                        name: "clusterRepulsion" + i + j,
                        force: this.createSingleClusterRepulsiveForce(clusterA, clusterB, strength),
                    };
                    forces.push(force);
                }
            }
        }
        return forces;
    }

    createSuperClusterRepulsiveForces() {
        let forces = [];
        let superClusters = this._superClusters;
        for (let i = 0; i < superClusters.length; i++) {
            const superClusterA = superClusters[i];
            for (let j = 0; j < superClusters.length; j++) {
                const superClusterB = superClusters[j];
                const itIsNotItself = i !== j;
                if (itIsNotItself) {
                    const strength = this._superClusterRepulsionStrength;
                    let force = {
                        name: "superClusterRepulsion" + i + j,
                        force: this.createSingleClusterRepulsiveForce(superClusterA, superClusterB, strength),
                    };
                    forces.push(force);
                }
            }
        }
        return forces;
    }

    createLinkForce() {
        let lS = this._linkParams.strength;
        let lD = this._linkParams.distance;
        let force = d3
            .forceLink()
            .strength(lS)
            .distance(lD)
            .id(d => d.id);
        return force;
    }

    createCenteringForce() {
        let center = this._gravitationalCenter;
        let force = d3.forceCenter(center.x, center.y);
        return force;
    }

    // Private:
    saveParams(cParams) {
        this._masterParams = cParams.masterPhysics;
        this._slaveParams = cParams.slavePhysics;
        this._linkParams = cParams.linkPhysics;
        this._positioningStrength = cParams.positioningStrength;
        this._clusterRepulsionStrength = cParams.clusterRepulsionStrength;
        this._superClusterRepulsionStrength = cParams.superClusterRepulsionStrength;
        this._chargeDistance = cParams.chargeDistance;
        this._chargeTheta = cParams.chargeTheta;
        this._collisionIterations = cParams.collisionIterations;
        this._activeNodes = cParams.activeNodes;
        this._inputNodes = cParams.inputeNodes;
        this._clusters = cParams.clusters;
        this._superClusters = cParams.superClusters;
        this._sharedNodesClusters = cParams.sharedNodesClusters;
    }

    createSingleRadialForce(cluster) {
        let cParams = {
            strength: this._slaveParams.radialStrength,
            cluster: cluster,
        };
        let forceGen = new RadialForceGenerator(cParams);
        let force = forceGen.compute();
        return force;
    }

    createSingleRadialReaction(slaveNode) {
        let cParams = {
            strength: this._slaveParams.radialStrength,
            slaveNode: slaveNode,
        };
        let forceGen = new RadialReactionGenerator(cParams);
        let force = forceGen.compute();
        return force;
    }

    createSingleClusterRepulsiveForce(clusterA, clusterB, strength) {
        let cParams = {
            strength: strength,
            clusterA: clusterA,
            clusterB: clusterB,
        };
        let forceGen = new ClusterRepulsiveForceGenerator(cParams);
        let force = forceGen.compute();
        return force;
    }

    createArrayForces(array, namingFunc, forceCreationFunc) {
        let forces = [];
        for (let i = 0; i < array.length; i++) {
            const cluster = array[i];
            let force = {
                name: namingFunc(i),
                force: forceCreationFunc(cluster),
            };
            forces.push(force);
        }
        return forces;
    }

    computeNodesSubsets() {
        let masterNodes = [];
        let slaveNodes = [];
        let nodes = this._activeNodes;
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            if (node.type === "master") {
                masterNodes.push(node);
            } else if (node.type === "slave") {
                slaveNodes.push(node);
            }
        }
        this._slaveNodes = slaveNodes;
        this._masterNodes = masterNodes;
    }

    setOriginAsGravitationalCenter() {
        const origin = {
            x: 0,
            y: 0,
        };
        this.setGravitationalCenter(origin);
    }
}
